/** Document-level land vs sea mask helpers (#269). */

import type { MapLandmass, MapLandmassWorld } from "./generated/zwMap";
import { sampleSmoothPolyline, type WorldPoint } from "./mapGeometry";
import { isNothing, just, nothing, type Maybe } from "./maybe";

/** Default mask resolution (long side). Soft brush stays usable; SVG may contour later. */
export const LANDMASS_DEFAULT_RESOLUTION = 1536;

/** Matches the editor background world span (±2000). */
export const LANDMASS_DEFAULT_WORLD: MapLandmassWorld = {
  x: -2000,
  y: -2000,
  w: 4000,
  h: 4000,
};

/** Pale ochre pencil wash over warm paper. */
export const LANDMASS_LAND_COLOR = "#e1cfaa";
export const LANDMASS_LAND_RGB = { r: 225, g: 207, b: 170 } as const;

/** Warm graphite, translucent enough to retain the paper beneath it. */
export const LANDMASS_COAST_INK = "rgba(65, 55, 43, 0.62)";

/** Soft water-side halo under the coast ink. */
export const LANDMASS_COAST_HALO = "rgba(65, 55, 43, 0.09)";

export const LANDMASS_BRUSH_SIZE_MIN = 1;
export const LANDMASS_BRUSH_SIZE_MAX = 400;
export const LANDMASS_BRUSH_SIZE_DEFAULT = 96;

/**
 * Coast jaggedness as an integer percent (0 = round, 100 = heavily fjorded).
 * Session-local Land tool setting; stamp math converts to 0..1.
 */
export const LANDMASS_COAST_ROUGHNESS_MIN = 0;
export const LANDMASS_COAST_ROUGHNESS_MAX = 100;
export const LANDMASS_COAST_ROUGHNESS_DEFAULT = 40;

/** Alpha threshold for binary land vs sea (kills soft brush AA fringes). */
export const LANDMASS_ALPHA_THRESHOLD = 128;

export type LandmassBrushMode = "add" | "erase" | "paint" | "cut";

/** Closed or open polyline in mask-pixel space. */
export type CoastPolyline = ReadonlyArray<{
  readonly x: number;
  readonly y: number;
}>;

export function clampLandmassBrushSize(value: number): number {
  if (Number.isFinite(value) === false) return LANDMASS_BRUSH_SIZE_DEFAULT;
  return Math.min(
    LANDMASS_BRUSH_SIZE_MAX,
    Math.max(LANDMASS_BRUSH_SIZE_MIN, value),
  );
}

/** Clamp a coast-jaggedness percent (0..100). */
export function clampLandmassCoastRoughness(value: number): number {
  if (Number.isFinite(value) === false) return LANDMASS_COAST_ROUGHNESS_DEFAULT;
  // Heal stale 0..1 fractions from before the percent UI (e.g. HMR / 0.4).
  const asPercent = value > 0 && value < 1 ? value * 100 : value;
  return Math.min(
    LANDMASS_COAST_ROUGHNESS_MAX,
    Math.max(LANDMASS_COAST_ROUGHNESS_MIN, Math.round(asPercent)),
  );
}

/** Percent → 0..1 factor used by stamp / cursor geometry. */
export function landmassCoastRoughness01(percent: number): number {
  return clampLandmassCoastRoughness(percent) / 100;
}

export function formatLandmassCoastRoughness(percent: number): string {
  return `${clampLandmassCoastRoughness(percent)}%`;
}

export function createDefaultLandmass(path: string): MapLandmass {
  return {
    path,
    width: LANDMASS_DEFAULT_RESOLUTION,
    height: LANDMASS_DEFAULT_RESOLUTION,
    world: { ...LANDMASS_DEFAULT_WORLD },
  };
}

/**
 * True for auto-generated landmass mask files under Assets/ (`landmass-<id>.…`).
 * Those are sculpt storage, not placeable map images.
 */
export function isMapLandmassAssetPath(relativePath: string): boolean {
  const normalized = relativePath.replace(/\\/g, "/");
  const slash = normalized.lastIndexOf("/");
  const name = slash >= 0 ? normalized.slice(slash + 1) : normalized;
  return /^landmass-/i.test(name);
}

/** Missing `visible` means shown (heal-on-read omits the default). */
export function isLandmassVisible(
  landmass: Pick<MapLandmass, "visible">,
): boolean {
  return landmass.visible !== false;
}

/** Missing `locked` means editable. */
export function isLandmassLocked(
  landmass: Pick<MapLandmass, "locked">,
): boolean {
  return landmass.locked === true;
}

/** World point → mask pixel (may be outside [0, w)×[0, h)). */
export function worldToMaskPixel(
  world: WorldPoint,
  landmass: Pick<MapLandmass, "width" | "height" | "world">,
): { x: number; y: number } {
  const { world: bounds, width, height } = landmass;
  const x = bounds.w === 0 ? 0 : ((world.x - bounds.x) / bounds.w) * width;
  const y = bounds.h === 0 ? 0 : ((world.y - bounds.y) / bounds.h) * height;
  return { x, y };
}

/** Brush radius in mask pixels for a world-space brush diameter. */
export function brushRadiusMaskPx(
  brushSizeWorld: number,
  landmass: Pick<MapLandmass, "width" | "height" | "world">,
): number {
  const worldPerPxX =
    landmass.width === 0 ? 1 : landmass.world.w / landmass.width;
  const worldPerPxY =
    landmass.height === 0 ? 1 : landmass.world.h / landmass.height;
  const worldPerPx = Math.max(worldPerPxX, worldPerPxY);
  return Math.max(0.5, brushSizeWorld / 2 / worldPerPx);
}

/** Deterministic 0..1 hash for integer lattice points. */
export function hashNoise2(ix: number, iy: number): number {
  let n = Math.imul(ix | 0, 374761393) ^ Math.imul(iy | 0, 668265263);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

/** Bilinear value noise in 0..1. */
export function valueNoise2(x: number, y: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const a = hashNoise2(x0, y0);
  const b = hashNoise2(x0 + 1, y0);
  const c = hashNoise2(x0, y0 + 1);
  const d = hashNoise2(x0 + 1, y0 + 1);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

/** Fractal Brownian motion in 0..1 (multi-octave coasts). */
export function fbm2(x: number, y: number, octaves = 4): number {
  let amp = 0.5;
  let freq = 1;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += amp * valueNoise2(x * freq, y * freq);
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return norm === 0 ? 0 : sum / norm;
}

/**
 * Radius along a brush angle, warped by noise so coasts stay crinkly
 * (Nortantis) and deterministic for a given mask-space center.
 * `roughness01` is 0..1 (0 = perfect circle radius).
 */
export function jaggedBrushRadius(
  baseRadius: number,
  angle: number,
  centerX: number,
  centerY: number,
  roughness01 = landmassCoastRoughness01(LANDMASS_COAST_ROUGHNESS_DEFAULT),
): number {
  const r = Math.min(1, Math.max(0, roughness01));
  if (r <= 0) return baseRadius;
  const nx = Math.cos(angle);
  const ny = Math.sin(angle);
  // Low roughness → broad lobes; high → fjords + micro-jags.
  const lobeFreq = 2.5 + r * 7;
  const fineFreq = 8 + r * 18;
  const lobes = fbm2(
    centerX * 0.07 + nx * lobeFreq,
    centerY * 0.07 + ny * lobeFreq,
    4,
  );
  const fine = fbm2(
    centerX * 0.28 + nx * fineFreq,
    centerY * 0.28 + ny * fineFreq,
    3,
  );
  const mixed = lobes * (1 - 0.45 * r) + fine * (0.2 + 0.55 * r);
  // Amplitude grows faster than linear so 20% vs 80% reads clearly.
  const amplitude = r * (0.65 + 0.55 * r);
  const warp = 1 + amplitude * (2 * mixed - 1);
  return Math.max(0.5, baseRadius * warp);
}

/** Vertex count for the jagged stamp (denser when coasts are rougher). */
export function jaggedBrushSteps(
  radiusMaskPx: number,
  roughness01 = landmassCoastRoughness01(LANDMASS_COAST_ROUGHNESS_DEFAULT),
): number {
  const r = Math.min(1, Math.max(0, roughness01));
  const density = 1.6 + r * 2.4;
  return Math.max(20, Math.min(144, Math.round(radiusMaskPx * density)));
}

type PathTracer = {
  beginPath: () => void;
  moveTo: (x: number, y: number) => void;
  lineTo: (x: number, y: number) => void;
  closePath: () => void;
  arc?: (x: number, y: number, radius: number, a0: number, a1: number) => void;
};

/**
 * Build a closed jagged coastline path for a brush stamp at mask-pixel center.
 */
export function traceJaggedBrushPath(
  ctx: PathTracer,
  centerX: number,
  centerY: number,
  radiusMaskPx: number,
  roughness01 = landmassCoastRoughness01(LANDMASS_COAST_ROUGHNESS_DEFAULT),
): void {
  const r = Math.min(1, Math.max(0, roughness01));
  ctx.beginPath();
  if (r <= 0.001) {
    // Perfect circle at 0% so the slider range is obvious.
    if (typeof ctx.arc === "function") {
      ctx.arc(centerX, centerY, radiusMaskPx, 0, Math.PI * 2);
    } else {
      const steps = 32;
      for (let i = 0; i <= steps; i++) {
        const angle = (i / steps) * Math.PI * 2;
        const px = centerX + Math.cos(angle) * radiusMaskPx;
        const py = centerY + Math.sin(angle) * radiusMaskPx;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
    }
    ctx.closePath();
    return;
  }
  const steps = jaggedBrushSteps(radiusMaskPx, r);
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * Math.PI * 2;
    const radius = jaggedBrushRadius(radiusMaskPx, angle, centerX, centerY, r);
    const px = centerX + Math.cos(angle) * radius;
    const py = centerY + Math.sin(angle) * radius;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

/**
 * Force a dirty brush region to binary land/sea so canvas AA cannot leave
 * soft paintbrush fringes. Preserves RGB on land so terrain tint survives.
 */
export function hardenMaskBrushRegion(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radiusMaskPx: number,
  roughness01 = landmassCoastRoughness01(LANDMASS_COAST_ROUGHNESS_DEFAULT),
): void {
  const coastRoughness = Math.min(1, Math.max(0, roughness01));
  const pad = Math.ceil(radiusMaskPx * (1 + coastRoughness * 1.2) + 3);
  const left = Math.max(0, Math.floor(centerX - pad));
  const top = Math.max(0, Math.floor(centerY - pad));
  const right = Math.min(ctx.canvas.width, Math.ceil(centerX + pad));
  const bottom = Math.min(ctx.canvas.height, Math.ceil(centerY + pad));
  const w = right - left;
  const h = bottom - top;
  if (w <= 0 || h <= 0) return;
  const image = ctx.getImageData(left, top, w, h);
  const { data } = image;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3] ?? 0;
    if (a >= LANDMASS_ALPHA_THRESHOLD) {
      data[i + 3] = 255;
    } else {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = 0;
    }
  }
  ctx.putImageData(image, left, top);
}

/** Parse `#rgb` / `#rrggbb` into 0..255 channels (falls back to parchment). */
export function cssColorToRgb(color: string): {
  r: number;
  g: number;
  b: number;
} {
  const raw = color.trim();
  const hex = raw.startsWith("#") ? raw.slice(1) : raw;
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    const r = hex[0] ?? "0";
    const g = hex[1] ?? "0";
    const b = hex[2] ?? "0";
    return {
      r: Number.parseInt(r + r, 16),
      g: Number.parseInt(g + g, 16),
      b: Number.parseInt(b + b, 16),
    };
  }
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    return {
      r: Number.parseInt(hex.slice(0, 2), 16),
      g: Number.parseInt(hex.slice(2, 4), 16),
      b: Number.parseInt(hex.slice(4, 6), 16),
    };
  }
  return { ...LANDMASS_LAND_RGB };
}

/**
 * Flood-fill contiguous land of the same color from a mask-pixel seed.
 * Sea (below alpha threshold) is a wall. Returns whether any pixel changed.
 */
export function floodFillLandColor(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  startX: number,
  startY: number,
  fill: { r: number; g: number; b: number },
): boolean {
  const sx = Math.floor(startX);
  const sy = Math.floor(startY);
  if (sx < 0 || sy < 0 || sx >= width || sy >= height) return false;
  const startIndex = (sy * width + sx) * 4;
  const startA = data[startIndex + 3] ?? 0;
  if (startA < LANDMASS_ALPHA_THRESHOLD) return false;
  const tr = data[startIndex] ?? 0;
  const tg = data[startIndex + 1] ?? 0;
  const tb = data[startIndex + 2] ?? 0;
  if (tr === fill.r && tg === fill.g && tb === fill.b) return false;

  const matches = (i: number): boolean => {
    const a = data[i + 3] ?? 0;
    if (a < LANDMASS_ALPHA_THRESHOLD) return false;
    return (
      (data[i] ?? 0) === tr &&
      (data[i + 1] ?? 0) === tg &&
      (data[i + 2] ?? 0) === tb
    );
  };

  const stack: number[] = [sx, sy];
  let changed = false;
  const visited = new Uint8Array(width * height);
  while (stack.length >= 2) {
    const y = stack.pop();
    const x = stack.pop();
    if (typeof x === "undefined" || typeof y === "undefined") break;
    if (x < 0 || y < 0 || x >= width || y >= height) continue;
    const pixel = y * width + x;
    if (visited[pixel] === 1) continue;
    visited[pixel] = 1;
    const i = pixel * 4;
    if (matches(i) === false) continue;
    data[i] = fill.r;
    data[i + 1] = fill.g;
    data[i + 2] = fill.b;
    data[i + 3] = 255;
    changed = true;
    stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
  }
  return changed;
}

export type TerrainBrushStyle = "jagged" | "feathered" | "pencil" | "mottled";
export type TerrainBrush = {
  style: TerrainBrushStyle;
  opacity: number;
  coverage: number;
  strokeSeed: number;
};
export const TERRAIN_BRUSH_OPTIONS = [
  { value: "jagged", label: "Jagged" },
  { value: "feathered", label: "Feathered" },
  { value: "pencil", label: "Pencil" },
  { value: "mottled", label: "Mottled" },
] as const;

let terrainTile: Maybe<{ key: string; canvas: HTMLCanvasElement }> = nothing;

/** Periodic noise prevents seams where the pigment tile repeats. */
function pigmentNoise(x: number, y: number, cells: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const a = hashNoise2(ix % cells, iy % cells);
  const b = hashNoise2((ix + 1) % cells, iy % cells);
  const c = hashNoise2(ix % cells, (iy + 1) % cells);
  const d = hashNoise2((ix + 1) % cells, (iy + 1) % cells);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

const PIGMENT_TILE_SIZE = 192;
let mottledNoise: Maybe<{ values: number[]; sorted: number[] }> = nothing;

/** Reuse one colored tile; mottled strokes randomize its placement once per gesture. */
function terrainPattern(
  ctx: CanvasRenderingContext2D,
  color: string,
  terrain: TerrainBrush,
): CanvasPattern | string {
  const coverage = Math.max(0, Math.min(100, terrain.coverage));
  const key = `${color}:${terrain.style}:${coverage}`;
  if (isNothing(terrainTile) || terrainTile.value.key !== key) {
    const canvas = document.createElement("canvas");
    const size = terrain.style === "mottled" ? PIGMENT_TILE_SIZE : 64;
    canvas.width = canvas.height = size;
    const paint = canvas.getContext("2d");
    if (paint === null) return color;
    paint.fillStyle = color;
    if (terrain.style === "mottled" && isNothing(mottledNoise)) {
      const values = Array.from({ length: size * size }, (_, index) => {
        const x = index % size;
        const y = Math.floor(index / size);
        return (
          pigmentNoise(x / 24, y / 24, 8) * 0.75 +
          pigmentNoise(x / 8, y / 8, 24) * 0.25
        );
      });
      mottledNoise = just({
        values,
        sorted: [...values].sort((a, b) => a - b),
      });
    }
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const tooth = hashNoise2(x, y);
        if (terrain.style === "mottled" && isNothing(mottledNoise) === false) {
          const { values, sorted } = mottledNoise.value;
          const threshold =
            sorted[
              Math.min(
                sorted.length - 1,
                Math.floor((sorted.length * coverage) / 100),
              )
            ]!;
          const noise = values[y * size + x]!;
          paint.globalAlpha =
            coverage === 100
              ? 1
              : Math.max(0, Math.min(1, (threshold - noise) / 0.045));
        } else {
          paint.globalAlpha = tooth < 0.22 ? 0 : 0.08 + tooth * 0.45;
        }
        paint.fillRect(x, y, 1, 1);
      }
    }
    terrainTile = just({ key, canvas });
  }
  const pattern = ctx.createPattern(terrainTile.value.canvas, "repeat");
  if (pattern === null) return color;
  if (terrain.style === "mottled") {
    pattern.setTransform(
      new DOMMatrix()
        .translate(
          hashNoise2(terrain.strokeSeed, 1) * PIGMENT_TILE_SIZE,
          hashNoise2(terrain.strokeSeed, 2) * PIGMENT_TILE_SIZE,
        )
        .rotate(hashNoise2(terrain.strokeSeed, 3) * 360),
    );
  }
  return pattern;
}

/**
 * Stamp land (jagged add), erase (radial), cut (jagged water), or terrain-paint.
 * Shoreline ink is drawn from contours; paint mode does not grow the mask.
 */
export function stampLandmassBrush(
  ctx: CanvasRenderingContext2D,
  landmass: Pick<MapLandmass, "width" | "height" | "world">,
  world: WorldPoint,
  brushSizeWorld: number,
  mode: LandmassBrushMode,
  /** Coast jaggedness percent 0..100 (Land add / Water cut stamps). */
  roughnessPercent = LANDMASS_COAST_ROUGHNESS_DEFAULT,
  /** Fill / terrain tint (Land add + Terrain paint). */
  fillColor = LANDMASS_LAND_COLOR,
  terrain: TerrainBrush = {
    style: "jagged",
    opacity: 100,
    coverage: 50,
    strokeSeed: 0,
  },
): void {
  if (mode === "cut") {
    stampLandmassCut(ctx, landmass, world, brushSizeWorld, roughnessPercent);
    return;
  }
  const { x, y } = worldToMaskPixel(world, landmass);
  const radius = brushRadiusMaskPx(brushSizeWorld, landmass);
  const roughness01 = landmassCoastRoughness01(roughnessPercent);
  ctx.imageSmoothingEnabled = false;
  if (mode === "add") {
    traceJaggedBrushPath(ctx, x, y, radius, roughness01);
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = fillColor;
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
    hardenMaskBrushRegion(ctx, x, y, radius, roughness01);
  } else if (mode === "paint") {
    ctx.save();
    ctx.globalCompositeOperation = "source-atop";
    ctx.globalAlpha = Math.max(0, Math.min(100, terrain.opacity)) / 100;
    if (terrain.style === "feathered") {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      const { r, g, b } = cssColorToRgb(fillColor);
      const wash = ctx.createRadialGradient(x, y, 0, x, y, radius);
      wash.addColorStop(0, `rgba(${r},${g},${b},0.6)`);
      wash.addColorStop(0.35, `rgba(${r},${g},${b},0.4)`);
      wash.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = wash;
    } else {
      traceJaggedBrushPath(ctx, x, y, radius, roughness01);
      ctx.fillStyle =
        terrain.style === "pencil" || terrain.style === "mottled"
          ? terrainPattern(ctx, fillColor, terrain)
          : fillColor;
    }
    ctx.fill();
    ctx.restore();
  } else {
    // Object Erase tool: smooth radial hole. Water lakes/rivers use "cut".
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.globalCompositeOperation = "destination-out";
    ctx.fillStyle = "#000";
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
    hardenMaskBrushRegion(ctx, x, y, radius, roughness01);
  }
}

/**
 * Jagged destination-out stamp for inland water (lakes / river samples).
 * Same FBM coastline as Land add; does not touch layer objects.
 */
export function stampLandmassCut(
  ctx: CanvasRenderingContext2D,
  landmass: Pick<MapLandmass, "width" | "height" | "world">,
  world: WorldPoint,
  brushSizeWorld: number,
  roughnessPercent = LANDMASS_COAST_ROUGHNESS_DEFAULT,
): void {
  const { x, y } = worldToMaskPixel(world, landmass);
  const radius = brushRadiusMaskPx(brushSizeWorld, landmass);
  const roughness01 = landmassCoastRoughness01(roughnessPercent);
  ctx.imageSmoothingEnabled = false;
  traceJaggedBrushPath(ctx, x, y, radius, roughness01);
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = "#000";
  ctx.fill();
  ctx.globalCompositeOperation = "source-over";
  hardenMaskBrushRegion(ctx, x, y, radius, roughness01);
}

/** Step factor matching Land stroke interpolation in useMapTools. */
export const RIVER_CORRIDOR_STEP_FACTOR = 0.35;

/**
 * River banks should read as gentle shoreline, not Land-add fjords.
 * Scales the Coastline slider down for corridor stamps only.
 */
export const RIVER_BANK_ROUGHNESS_FACTOR = 0.35;

/**
 * Densify an open polyline along straight chords (stamps overlap).
 * Rivers use {@link densifyRiverCenterline} instead.
 */
export function densifyPolyline(
  points: ReadonlyArray<WorldPoint>,
  stepWorld: number,
): WorldPoint[] {
  if (points.length === 0) return [];
  const first = points[0];
  if (typeof first === "undefined") return [];
  if (points.length === 1) return [{ x: first.x, y: first.y }];
  const step = Math.max(0.5, stepWorld);
  const out: WorldPoint[] = [{ x: first.x, y: first.y }];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (typeof a === "undefined" || typeof b === "undefined") continue;
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    if (dist < 1e-6) continue;
    const count = Math.max(1, Math.ceil(dist / step));
    for (let s = 1; s <= count; s++) {
      const t = s / count;
      out.push({
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
      });
    }
  }
  return out;
}

/** Smooth Catmull-Rom samples through river anchors (same bends as path ink). */
export function densifyRiverCenterline(
  points: ReadonlyArray<WorldPoint>,
  stepWorld: number,
): WorldPoint[] {
  return sampleSmoothPolyline(points, stepWorld);
}

/** Coastline percent → gentler river-bank jaggedness. */
export function riverBankRoughnessPercent(coastPercent: number): number {
  return clampLandmassCoastRoughness(
    clampLandmassCoastRoughness(coastPercent) * RIVER_BANK_ROUGHNESS_FACTOR,
  );
}

/**
 * Carve a constant-width water corridor along a smooth centerline through
 * the author anchors (not chord-to-chord). Banks use softened jaggedness.
 */
export function stampRiverCorridor(
  ctx: CanvasRenderingContext2D,
  landmass: Pick<MapLandmass, "width" | "height" | "world">,
  points: ReadonlyArray<WorldPoint>,
  brushSizeWorld: number,
  roughnessPercent = LANDMASS_COAST_ROUGHNESS_DEFAULT,
): void {
  if (points.length < 2) return;
  const radiusWorld = brushSizeWorld / 2;
  const samples = densifyRiverCenterline(
    points,
    Math.max(0.5, radiusWorld * RIVER_CORRIDOR_STEP_FACTOR),
  );
  const bankRoughness = riverBankRoughnessPercent(roughnessPercent);
  for (const sample of samples) {
    stampLandmassCut(ctx, landmass, sample, brushSizeWorld, bankRoughness);
  }
}

export type RiverForkAttach = {
  readonly point: WorldPoint;
  readonly distance: number;
  readonly branchIndex: number;
};

/**
 * Closest point on an open polyline (including vertices).
 * Returns Nothing when the polyline is empty.
 */
export function closestPointOnPolyline(
  point: WorldPoint,
  polyline: ReadonlyArray<WorldPoint>,
): Maybe<{ point: WorldPoint; distance: number }> {
  if (polyline.length === 0) return nothing;
  if (polyline.length === 1) {
    const only = polyline[0];
    if (typeof only === "undefined") return nothing;
    const d = Math.hypot(point.x - only.x, point.y - only.y);
    return just({ point: { x: only.x, y: only.y }, distance: d });
  }
  let bestPoint = { x: polyline[0]!.x, y: polyline[0]!.y };
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i]!;
    const b = polyline[i + 1]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    let proj: WorldPoint;
    if (dx === 0 && dy === 0) {
      proj = { x: a.x, y: a.y };
    } else {
      const t = Math.max(
        0,
        Math.min(
          1,
          ((point.x - a.x) * dx + (point.y - a.y) * dy) / (dx * dx + dy * dy),
        ),
      );
      proj = { x: a.x + t * dx, y: a.y + t * dy };
    }
    const d = Math.hypot(point.x - proj.x, point.y - proj.y);
    if (d < bestDist) {
      bestDist = d;
      bestPoint = proj;
    }
  }
  return just({ point: bestPoint, distance: bestDist });
}

/**
 * Find a fork attachment on any draft branch within `thresholdWorld`.
 * Prefer the nearest hit across all branches.
 */
export function findRiverForkAttach(
  point: WorldPoint,
  branches: ReadonlyArray<ReadonlyArray<WorldPoint>>,
  thresholdWorld: number,
): Maybe<RiverForkAttach> {
  let best: Maybe<RiverForkAttach> = nothing;
  for (let i = 0; i < branches.length; i++) {
    const branch = branches[i];
    if (typeof branch === "undefined" || branch.length === 0) continue;
    const hit = closestPointOnPolyline(point, branch);
    if (isNothing(hit)) continue;
    if (hit.value.distance > thresholdWorld) continue;
    if (isNothing(best) || hit.value.distance < best.value.distance) {
      best = just({
        point: hit.value.point,
        distance: hit.value.distance,
        branchIndex: i,
      });
    }
  }
  return best;
}

function isLandAlpha(alpha: number): boolean {
  return alpha >= LANDMASS_ALPHA_THRESHOLD;
}

function sampleAlpha(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  x: number,
  y: number,
): number {
  if (x < 0 || y < 0 || x >= width || y >= height) return 0;
  return data[(y * width + x) * 4 + 3] ?? 0;
}

type Segment = {
  ax: number;
  ay: number;
  bx: number;
  by: number;
};

/** Marching-squares edge cases: bit 1=TL, 2=TR, 4=BR, 8=BL. */
function cellSegments(
  caseIndex: number,
  x: number,
  y: number,
): ReadonlyArray<Segment> {
  const tl = { x, y };
  const tr = { x: x + 1, y };
  const br = { x: x + 1, y: y + 1 };
  const bl = { x, y: y + 1 };
  const top = { x: x + 0.5, y };
  const right = { x: x + 1, y: y + 0.5 };
  const bottom = { x: x + 0.5, y: y + 1 };
  const left = { x, y: y + 0.5 };
  switch (caseIndex) {
    case 0:
    case 15:
      return [];
    case 1:
    case 14:
      return [{ ax: left.x, ay: left.y, bx: top.x, by: top.y }];
    case 2:
    case 13:
      return [{ ax: top.x, ay: top.y, bx: right.x, by: right.y }];
    case 3:
    case 12:
      return [{ ax: left.x, ay: left.y, bx: right.x, by: right.y }];
    case 4:
    case 11:
      return [{ ax: right.x, ay: right.y, bx: bottom.x, by: bottom.y }];
    case 5:
      return [
        { ax: left.x, ay: left.y, bx: top.x, by: top.y },
        { ax: right.x, ay: right.y, bx: bottom.x, by: bottom.y },
      ];
    case 6:
    case 9:
      return [{ ax: top.x, ay: top.y, bx: bottom.x, by: bottom.y }];
    case 7:
    case 8:
      return [{ ax: left.x, ay: left.y, bx: bottom.x, by: bottom.y }];
    case 10:
      return [
        { ax: top.x, ay: top.y, bx: right.x, by: right.y },
        { ax: left.x, ay: left.y, bx: bottom.x, by: bottom.y },
      ];
    default:
      void tl;
      void tr;
      void br;
      void bl;
      return [];
  }
}

function pointKey(x: number, y: number): string {
  return `${x.toFixed(2)},${y.toFixed(2)}`;
}

/**
 * Extract shoreline polylines from RGBA image data (mask-pixel space).
 * A virtual transparent border closes coasts even when land touches the mask edge.
 */
export function extractCoastPolylinesFromRgba(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): CoastPolyline[] {
  if (width < 1 || height < 1) return [];
  const segments: Segment[] = [];
  for (let y = -1; y < height; y++) {
    for (let x = -1; x < width; x++) {
      const tl = isLandAlpha(sampleAlpha(data, width, height, x, y));
      const tr = isLandAlpha(sampleAlpha(data, width, height, x + 1, y));
      const br = isLandAlpha(sampleAlpha(data, width, height, x + 1, y + 1));
      const bl = isLandAlpha(sampleAlpha(data, width, height, x, y + 1));
      const caseIndex =
        (tl ? 1 : 0) | (tr ? 2 : 0) | (br ? 4 : 0) | (bl ? 8 : 0);
      if (caseIndex === 0 || caseIndex === 15) continue;
      for (const seg of cellSegments(caseIndex, x, y)) segments.push(seg);
    }
  }
  return stitchSegments(segments);
}

/** Extract shoreline polylines from a binary land mask canvas. */
export function extractCoastPolylines(
  canvas: HTMLCanvasElement,
): CoastPolyline[] {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (ctx === null) return [];
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  return extractCoastPolylinesFromRgba(data, width, height);
}

function stitchSegments(segments: Segment[]): CoastPolyline[] {
  if (segments.length === 0) return [];
  type Node = { x: number; y: number; links: number[] };
  const nodes = new Map<string, Node>();
  const ensure = (x: number, y: number): Node => {
    const key = pointKey(x, y);
    const existing = nodes.get(key);
    if (typeof existing !== "undefined") return existing;
    const created: Node = { x, y, links: [] };
    nodes.set(key, created);
    return created;
  };
  const edges: Array<{ a: Node; b: Node; used: boolean }> = [];
  for (const seg of segments) {
    const a = ensure(seg.ax, seg.ay);
    const b = ensure(seg.bx, seg.by);
    if (a === b) continue;
    const edgeIndex = edges.length;
    edges.push({ a, b, used: false });
    a.links.push(edgeIndex);
    b.links.push(edgeIndex);
  }

  const polylines: CoastPolyline[] = [];
  for (let i = 0; i < edges.length; i++) {
    const startEdge = edges[i];
    if (typeof startEdge === "undefined" || startEdge.used) continue;
    const line: Array<{ x: number; y: number }> = [];
    const edge = startEdge;
    const from = edge.a;
    let to = edge.b;
    edge.used = true;
    line.push({ x: from.x, y: from.y });
    line.push({ x: to.x, y: to.y });
    let guard = edges.length + 2;
    while (guard-- > 0) {
      const nextIndex = to.links.find((idx) => {
        const candidate = edges[idx];
        return typeof candidate !== "undefined" && candidate.used === false;
      });
      if (typeof nextIndex === "undefined") break;
      const next = edges[nextIndex];
      if (typeof next === "undefined") break;
      next.used = true;
      const advance = next.a === to ? next.b : next.a;
      line.push({ x: advance.x, y: advance.y });
      to = advance;
    }
    if (line.length >= 2) polylines.push(line);
  }
  return polylines;
}

/** Smooth raster stair steps in source space, independent of pan and zoom. */
export function smoothCoastPolyline(poly: CoastPolyline): CoastPolyline {
  if (poly.length < 6) return poly;
  const first = poly[0]!;
  const last = poly[poly.length - 1]!;
  const closed = first.x === last.x && first.y === last.y;
  const points = closed ? poly.slice(0, -1) : poly;
  return points.map((point, index) => {
    if (closed === false && (index < 2 || index >= points.length - 2))
      return point;
    let x = 0;
    let y = 0;
    for (let offset = -2; offset <= 2; offset++) {
      const sample = points[(index + offset + points.length) % points.length]!;
      const weight = 3 - Math.abs(offset);
      x += sample.x * weight;
      y += sample.y * weight;
    }
    // Retain half the original contour so smoothing removes pixel corners
    // without ironing out the coastline’s small inlets and headlands.
    return { x: (point.x + x / 9) / 2, y: (point.y + y / 9) / 2 };
  });
}

// Cache geometry, not screen pixels: panning, zooming and export reuse the bands.
const waveContours = new WeakMap<readonly CoastPolyline[], CoastPolyline[][]>();

function coastalWaveContours(
  coasts: readonly CoastPolyline[],
  width: number,
  height: number,
): CoastPolyline[][] {
  if (coasts.length === 0) return [];
  const cached = waveContours.get(coasts);
  if (typeof cached !== "undefined") return cached;
  const padding = 16;
  const canvas = document.createElement("canvas");
  canvas.width = width + padding * 2;
  canvas.height = height + padding * 2;
  const paint = canvas.getContext("2d");
  if (paint === null) return [];
  const shape = new Path2D();
  for (const poly of coasts) {
    const first = poly[0];
    if (typeof first === "undefined") continue;
    shape.moveTo(first.x + padding, first.y + padding);
    for (const point of poly.slice(1))
      shape.lineTo(point.x + padding, point.y + padding);
    shape.closePath();
  }
  paint.lineJoin = "round";
  paint.lineCap = "round";
  // Dilating the whole land silhouette merges nearby shores naturally and
  // shrinks lake contours without self-intersecting polygon offsets.
  const bands = [3, 7, 12].map((distance) => {
    paint.clearRect(0, 0, canvas.width, canvas.height);
    paint.fill(shape, "evenodd");
    paint.lineWidth = distance * 2;
    paint.stroke(shape);
    return extractCoastPolylines(canvas).map((poly) =>
      poly.map((point) => ({ x: point.x - padding, y: point.y - padding })),
    );
  });
  waveContours.set(coasts, bands);
  return bands;
}

function coastPath(
  coasts: readonly CoastPolyline[],
  project: (x: number, y: number) => WorldPoint,
): Path2D {
  const outline = new Path2D();
  for (const poly of coasts) {
    const points = smoothCoastPolyline(poly).map((p) => project(p.x, p.y));
    const first = points[0];
    const last = points[points.length - 1];
    if (typeof first === "undefined" || typeof last === "undefined") continue;
    outline.moveTo((last.x + first.x) / 2, (last.y + first.y) / 2);
    for (let i = 0; i < points.length; i++) {
      const p = points[i]!;
      const next = points[(i + 1) % points.length]!;
      outline.quadraticCurveTo(
        p.x,
        p.y,
        (p.x + next.x) / 2,
        (p.y + next.y) / 2,
      );
    }
    outline.closePath();
  }
  return outline;
}

/** Immediate brush feedback; defer contour extraction until the stroke ends. */
export function drawLandmassPreview(
  ctx: CanvasRenderingContext2D,
  mask: CanvasImageSource,
  landmass: Pick<MapLandmass, "world">,
  toScreen: (point: WorldPoint) => WorldPoint,
): void {
  const { world } = landmass;
  const topLeft = toScreen({ x: world.x, y: world.y });
  const bottomRight = toScreen({ x: world.x + world.w, y: world.y + world.h });
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    mask,
    topLeft.x,
    topLeft.y,
    bottomRight.x - topLeft.x,
    bottomRight.y - topLeft.y,
  );
  ctx.restore();
}

/**
 * Draw a smoothed color wash and a light graphite shoreline.
 * `coasts` are mask-pixel polylines from `extractCoastPolylines`.
 */
export function drawLandmassMask(
  ctx: CanvasRenderingContext2D,
  mask: CanvasImageSource,
  landmass: Pick<MapLandmass, "width" | "height" | "world">,
  toScreen: (p: WorldPoint) => { x: number; y: number },
  coasts: readonly CoastPolyline[],
): void {
  const { world, width: maskW, height: maskH } = landmass;
  const topLeft = toScreen({ x: world.x, y: world.y });
  const bottomRight = toScreen({
    x: world.x + world.w,
    y: world.y + world.h,
  });
  const w = bottomRight.x - topLeft.x;
  const h = bottomRight.y - topLeft.y;
  if (w === 0 || h === 0) return;

  // Marching-squares samples pixel (i,j) as a corner at (i,j), so edge
  // midpoints sit half a pixel inside the filled squares `drawImage` paints
  // (which cover [i,i+1]×[j,j+1]). Shift +0.5 so coasts ride the pixel edges.
  const maskToScreen = (px: number, py: number): { x: number; y: number } => {
    const wx = world.x + ((px + 0.5) / maskW) * world.w;
    const wy = world.y + ((py + 0.5) / maskH) * world.h;
    return toScreen({ x: wx, y: wy });
  };

  const outline = coastPath(coasts, maskToScreen);
  const scale = Math.max(Math.abs(w) / maskW, Math.abs(h) / maskH);
  ctx.save();
  const water = new Path2D();
  water.rect(0, 0, ctx.canvas.width, ctx.canvas.height);
  water.addPath(outline);
  ctx.clip(water, "evenodd");
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.lineWidth = Math.max(0.45, Math.min(1.05, scale * 0.3));
  coastalWaveContours(coasts, maskW, maskH).forEach((band, index) => {
    ctx.strokeStyle = `rgba(76, 67, 49, ${0.32 - index * 0.065})`;
    ctx.stroke(coastPath(band, maskToScreen));
  });
  ctx.restore();
  ctx.save();
  ctx.clip(outline, "evenodd");
  ctx.fillStyle = LANDMASS_LAND_COLOR;
  ctx.fillRect(topLeft.x, topLeft.y, w, h);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.globalAlpha = 0.9;
  ctx.drawImage(mask, topLeft.x, topLeft.y, w, h);
  ctx.restore();

  ctx.save();
  const coastWidth = Math.max(0.65, Math.min(1.6, scale * 0.55));
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.strokeStyle = LANDMASS_COAST_HALO;
  ctx.lineWidth = coastWidth * 3;
  ctx.stroke(outline);
  ctx.strokeStyle = LANDMASS_COAST_INK;
  ctx.lineWidth = coastWidth;
  ctx.stroke(outline);
  ctx.restore();
}

/** Encode an offscreen canvas as a PNG data URL. */
export function canvasToPngDataUrl(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL("image/png");
}

export function dataUrlToBase64(dataUrl: string): string {
  const marker = "base64,";
  const idx = dataUrl.indexOf(marker);
  if (idx < 0) return dataUrl;
  return dataUrl.slice(idx + marker.length);
}

export function createEmptyLandmassCanvas(
  width: number,
  height: number,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(width));
  canvas.height = Math.max(1, Math.floor(height));
  return canvas;
}

export function cloneCanvasImageData(
  canvas: HTMLCanvasElement,
): Maybe<ImageData> {
  const ctx = canvas.getContext("2d");
  if (ctx === null) return nothing;
  return just(ctx.getImageData(0, 0, canvas.width, canvas.height));
}

export function applyCanvasImageData(
  canvas: HTMLCanvasElement,
  data: ImageData,
): void {
  const ctx = canvas.getContext("2d");
  if (ctx === null) return;
  if (canvas.width !== data.width || canvas.height !== data.height) {
    canvas.width = data.width;
    canvas.height = data.height;
  }
  ctx.putImageData(data, 0, 0);
}
